<template>
  <div class="subscriptions-page">
    <div class="page-title-bar">
      <h2 class="page-title">HM Care Subscriptions</h2>
    </div>

    <div class="page-content">
      <div class="search-bar">
        <div class="search-wrapper" :class="{ 'is-open': showDropdown }">
          <div
            class="search-select-wrapper"
            :class="{ 'is-open': showDropdown }"
            @click="toggleDropdown"
          >
            <span class="search-select-label">{{ currentSearchTypeLabel }}</span>
            <img :src="downarrowIcon" alt="" class="dropdown-arrow" />
          </div>
          <input
            v-model="searchKeyword"
            type="text"
            class="search-input"
            maxlength="50"
            placeholder="Search.."
            @keyup.enter="handleSearch"
          />
          <div v-if="showDropdown" class="dropdown-menu">
            <div
              v-for="option in searchTypeOptions"
              :key="option.value"
              class="dropdown-item"
              :class="{ selected: searchType === option.value }"
              @click="selectSearchType(option.value)"
            >
              {{ option.label }}
            </div>
          </div>
        </div>
        <div class="search-buttons">
          <button type="button" class="btn btn-search" @click="handleSearch">Search</button>
          <button type="button" class="btn btn-clear" @click="handleClear">Clear</button>
        </div>
      </div>

      <div class="action-bar">
        <span
          class="count-info"
          :class="{ 'count-info--pending': pageView === 'loading' }"
        >
          {{ pageView === 'loading' ? 0 : total }} Subscriptions
        </span>
      </div>

      <div class="list-body" :aria-busy="pageView === 'loading'">
        <LoadingIndicator v-if="pageView === 'loading'" />

        <div
          v-else-if="pageView === 'content' && total === 0"
          class="orders-empty"
        >
          <img :src="imgListBlank" class="orders-empty-img" alt="" />
          <p class="orders-empty-msg">No subscriptions found</p>
        </div>

        <div v-else-if="pageView === 'content' && total > 0" class="table-section">
          <div class="table-scroll">
            <table class="subs-table">
              <colgroup>
                <col class="col-mobile" />
                <col class="col-address" />
                <col class="col-hm-care" />
                <col class="col-effective" />
                <col class="col-status" />
                <col class="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th class="th-start th-mobile">Registered Mobile</th>
                  <th class="th-address">Service Address for Subscription</th>
                  <th class="th-center th-hm-care">HM Care</th>
                  <th class="th-center th-effective">Effective Time</th>
                  <th class="th-center th-status">Status</th>
                  <th class="th-end th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.id">
                  <td class="td-left td-mobile">{{ row.registeredMobile }}</td>
                  <td class="td-left td-address">{{ row.serviceAddress }}</td>
                  <td class="td-center td-hm-care">{{ row.hmCare }}</td>
                  <td class="td-center td-effective">{{ row.effectiveTime }}</td>
                  <td class="td-center td-status">
                    <span
                      class="status-badge"
                      :class="{
                        inactive:
                          row.status === 'Inactive' || row.status === 'Inactive (Payment Failed)',
                        'status-stacked':
                          row.status === 'Active (Ending Soon)' ||
                          row.status === 'Inactive (Payment Failed)',
                        'ending-soon': row.status === 'Active (Ending Soon)',
                      }"
                    >
                      <template v-if="row.status === 'Active (Ending Soon)'">
                        <span class="status-stacked-line">Active</span>
                        <span class="status-stacked-line">(Ending Soon)</span>
                      </template>
                      <template v-else-if="row.status === 'Inactive (Payment Failed)'">
                        <span class="status-stacked-line">Inactive</span>
                        <span class="status-stacked-line">(Payment Failed)</span>
                      </template>
                      <template v-else>{{ row.status }}</template>
                    </span>
                  </td>
                  <td class="td-actions">
                    <div class="actions">
                      <button type="button" class="action-icon-btn" @click="onView(row)">
                        <img :src="viewIcon" alt="" />
                      </button>
                      <button
                        type="button"
                        class="action-icon-btn"
                        :class="{ disabled: !row.canCancel }"
                        :disabled="!row.canCancel"
                        @click="onCancel(row)"
                      >
                        <img
                          :src="row.canCancel ? cancelIcon : cancelIconDisabled"
                          alt=""
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="pagination-wrapper">
        <PageComponent
          v-if="pageView === 'content' && total > 0"
          :current-page="currentPage"
          :total-pages="totalPages"
          @page-change="onPageChange"
        />
      </div>
    </div>

    <SubscriptionDetail
      :open="detailOpen"
      :detail="selectedDetail"
      @close="closeDetail"
      @cancel="onDetailCancel"
      @view-linked-orders="onViewLinkedOrders"
    />

    <CancelSubscriptionModal
      :open="cancelModalOpen"
      :plan="cancelModalPlan"
      @close="closeCancelModal"
      @submit="onCancelSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import SubscriptionDetail, {
  type SubscriptionDetailData,
} from './Detail.vue'
import CancelSubscriptionModal, {
  type CancelEffectiveTime,
} from './CancelSubscriptionModal.vue'
import {
  careOrdersListRouteForSub,
  careOrdersSubscriptionDetailRoute,
  isCareOrdersSubscriptionDetailRoute,
} from '@/utils/careOrdersRoutes'
import { writeHmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import {
  buildCareSubscriptionSearchTypes,
  cancelCareSubscription,
  DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES,
  getCareSubscriptionDetail,
  mapCareSubscriptionDetailToView,
  mapCareSubscriptionListItemToRow,
  postCareSubscriptionLinkedOrders,
  postCareSubscriptionMeta,
  readCareSubscriptionListPrefill,
  searchCareSubscriptionList,
  type CareSubscriptionRowView,
  type CareSubscriptionSearchTypeOption,
} from '@/api/careSubscription'
import downarrowIcon from '@/assets/downarrow@2x.png'
import imgListBlank from '@/assets/list_blank@2x.png'
import viewIcon from '@/assets/view@2x.png'
import cancelIcon from '@/assets/cancel subscription@2x.png'
import cancelIconDisabled from '@/assets/cancel subscription.white@2x.png'

type PageView = 'loading' | 'content'

const PAGE_SIZE = 10
const LOADING_FLASH_MS = 280

const route = useRoute()
const router = useRouter()

const pageView = ref<PageView>('loading')
const showDropdown = ref(false)
const searchTypeOptions = ref<CareSubscriptionSearchTypeOption[]>([
  ...DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES,
])
const searchType = ref(DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES[0]?.value ?? 'subscription_id')
const searchKeyword = ref('')
const currentPage = ref(1)
const total = ref(0)
const totalPagesFromApi = ref(1)
const rows = ref<CareSubscriptionRowView[]>([])
let fetchGeneration = 0
let detailSyncGeneration = 0

const detailOpen = ref(false)
const selectedDetail = ref<SubscriptionDetailData | null>(null)
const cancelModalOpen = ref(false)
const cancelTargetId = ref<number | null>(null)
const cancelModalPlan = ref('')

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => {},
)

const currentSearchTypeLabel = computed(() => {
  if (searchType.value === 'contact_phone_number') return 'Phone'
  const hit = searchTypeOptions.value.find((o) => o.value === searchType.value)
  return hit?.label ?? (
    searchType.value === 'home_id'
      ? 'Home ID'
      : searchType.value === 'user_id'
        ? 'User ID'
        : 'Sub. ID'
  )
})

const totalPages = computed(() => Math.max(1, totalPagesFromApi.value))

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function closeDropdown(): void {
  showDropdown.value = false
}

function toggleDropdown(): void {
  showDropdown.value = !showDropdown.value
}

function ensureSearchTypeValid(): void {
  if (searchTypeOptions.value.some((o) => o.value === searchType.value)) return
  searchType.value = searchTypeOptions.value[0]?.value ?? 'subscription_id'
}

function selectSearchType(type: string): void {
  if (searchType.value === type) {
    showDropdown.value = false
    return
  }
  searchType.value = type
  showDropdown.value = false
}

function applyListPagination(pagination: { total?: number; total_pages?: number } | undefined): void {
  total.value = pagination?.total ?? 0
  totalPagesFromApi.value = Math.max(1, pagination?.total_pages ?? 1)
}

function resetListState(): void {
  rows.value = []
  total.value = 0
  totalPagesFromApi.value = 1
}

function rowToDetail(row: CareSubscriptionRowView): SubscriptionDetailData {
  return {
    id: row.id,
    subscriptionId: row.subId,
    userId: row.userId,
    homeId: row.homeId,
    registeredMobile: row.registeredMobile,
    plan: row.hmCare,
    status: row.status,
    canCancel: row.canCancel,
    effectiveTime: row.effectiveTime,
    serviceAddress: row.serviceAddress,
    contactPerson: row.contactPerson,
    phoneNumber: row.contactPhone,
    cancellationTime: row.cancellationTime,
    endTimeLine1: row.endTimeLine1,
    endTimeLine2: row.endTimeLine2,
  }
}

async function loadSubscriptionMeta(): Promise<void> {
  try {
    const response = await postCareSubscriptionMeta({ page: 1, page_size: PAGE_SIZE })
    if (response.code === 200) {
      searchTypeOptions.value = buildCareSubscriptionSearchTypes(response.data)
      ensureSearchTypeValid()
      return
    }
  } catch (err) {
    console.error('Failed to load care subscription meta:', err)
  }
  searchTypeOptions.value = [...DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES]
  ensureSearchTypeValid()
}

async function fetchSubscriptionsData(): Promise<void> {
  const generation = ++fetchGeneration
  try {
    const keyword = searchKeyword.value.trim()
    const params: {
      page: number
      page_size: number
      search_type?: string
      keyword?: string
    } = {
      page: currentPage.value,
      page_size: PAGE_SIZE,
    }
    if (keyword) {
      params.search_type = searchType.value
      params.keyword = keyword
    }

    const response = await searchCareSubscriptionList(params)
    if (generation !== fetchGeneration) return

    if (response.code === 200) {
      rows.value = (response.data?.list || []).map(mapCareSubscriptionListItemToRow)
      applyListPagination(response.data?.pagination)
    } else {
      resetListState()
      showToast('error', response.msg || 'Failed to load subscriptions')
    }
  } catch (err) {
    if (generation !== fetchGeneration) return
    console.error('Network error:', err)
    resetListState()
    showToast('error', 'Network request failed')
  }
}

function tryConsumeSubscriptionListPrefill(): void {
  const prefill = readCareSubscriptionListPrefill()
  if (!prefill) return
  searchKeyword.value = prefill.keyword
  currentPage.value = 1
  if (searchTypeOptions.value.some((o) => o.value === prefill.searchType)) {
    searchType.value = prefill.searchType
    return
  }
  // meta 未列出时仍按 Users 传入的 home_id / user_id 请求
  if (prefill.searchType === 'home_id' || prefill.searchType === 'user_id') {
    searchType.value = prefill.searchType
  }
}

async function beginDataLoad(): Promise<void> {
  pageView.value = 'loading'
  try {
    await loadSubscriptionMeta()
    tryConsumeSubscriptionListPrefill()
    await wait(LOADING_FLASH_MS)
    await fetchSubscriptionsData()
  } finally {
    pageView.value = 'content'
  }
}

async function refreshSubscriptions(): Promise<void> {
  pageView.value = 'loading'
  rows.value = []
  try {
    await wait(LOADING_FLASH_MS)
    await fetchSubscriptionsData()
  } finally {
    pageView.value = 'content'
  }
}

function handleSearch(): void {
  closeDropdown()
  if (searchKeyword.value.length > 50) {
    showToast('error', 'Search keyword cannot exceed 50 characters')
    return
  }
  currentPage.value = 1
  void refreshSubscriptions()
}

function handleClear(): void {
  searchType.value = searchTypeOptions.value[0]?.value ?? 'subscription_id'
  searchKeyword.value = ''
  currentPage.value = 1
  showDropdown.value = false
  void refreshSubscriptions()
}

function onPageChange(page: number): void {
  currentPage.value = page
  void refreshSubscriptions()
}

function onView(row: CareSubscriptionRowView): void {
  void router.push(careOrdersSubscriptionDetailRoute(row.subId))
}

function closeDetail(): void {
  if (isCareOrdersSubscriptionDetailRoute(route)) {
    void router.push(careOrdersListRouteForSub('hm_care_subscription'))
    return
  }
  detailOpen.value = false
  selectedDetail.value = null
}

async function resolvePrimaryIdBySubscriptionId(subscriptionId: string): Promise<number | null> {
  const fromRows = rows.value.find((r) => r.subId === subscriptionId)
  if (fromRows) return fromRows.id

  try {
    const response = await searchCareSubscriptionList({
      page: 1,
      page_size: 1,
      search_type: 'subscription_id',
      keyword: subscriptionId,
    })
    if (response.code === 200) {
      const hit = (response.data?.list || []).find(
        (item) =>
          String(item.subscription_id ?? item.sub_id ?? '').trim() === subscriptionId ||
          String(item.id) === subscriptionId,
      )
      if (hit) return Number(hit.id)
      const first = response.data?.list?.[0]
      if (first) return Number(first.id)
    }
  } catch (err) {
    console.error('Failed to resolve subscription id:', err)
  }
  return null
}

async function syncDetailFromRoute(): Promise<void> {
  const generation = ++detailSyncGeneration
  if (!isCareOrdersSubscriptionDetailRoute(route)) {
    detailOpen.value = false
    selectedDetail.value = null
    return
  }

  const subscriptionId = String(route.params.subscriptionId ?? '').trim()
  if (!subscriptionId) {
    void router.replace(careOrdersListRouteForSub('hm_care_subscription'))
    return
  }

  const primaryId = await resolvePrimaryIdBySubscriptionId(subscriptionId)
  if (generation !== detailSyncGeneration) return
  if (primaryId == null || !Number.isFinite(primaryId)) {
    showToast('error', 'Subscription not found')
    void router.replace(careOrdersListRouteForSub('hm_care_subscription'))
    return
  }

  try {
    const response = await getCareSubscriptionDetail(primaryId)
    if (generation !== detailSyncGeneration) return
    if (response.code !== 200 || !response.data) {
      showToast('error', response.msg || 'Failed to load subscription detail')
      void router.replace(careOrdersListRouteForSub('hm_care_subscription'))
      return
    }
    selectedDetail.value = rowToDetail(mapCareSubscriptionDetailToView(response.data))
    detailOpen.value = true
  } catch (err) {
    if (generation !== detailSyncGeneration) return
    console.error('Failed to load subscription detail:', err)
    showToast('error', 'Network request failed')
    void router.replace(careOrdersListRouteForSub('hm_care_subscription'))
  }
}

watch(
  () => [route.name, route.params.subscriptionId] as const,
  () => {
    void syncDetailFromRoute()
  },
  { immediate: true },
)

function openCancelModal(row: CareSubscriptionRowView): void {
  if (!row.canCancel) return
  cancelTargetId.value = row.id
  cancelModalPlan.value = row.hmCare
  cancelModalOpen.value = true
}

function closeCancelModal(): void {
  cancelModalOpen.value = false
  cancelTargetId.value = null
  cancelModalPlan.value = ''
}

async function onCancelSubmit(effectiveTime: CancelEffectiveTime): Promise<void> {
  const id = cancelTargetId.value
  if (id == null) {
    closeCancelModal()
    return
  }

  try {
    const response = await cancelCareSubscription({
      id,
      cancellation_mode: effectiveTime,
    })
    if (response.code !== 200) {
      showToast('error', response.msg || 'Failed to cancel subscription')
      return
    }
    closeCancelModal()
    showToast('success', 'Successfully canceled')
    await refreshSubscriptions()
    if (selectedDetail.value && selectedDetail.value.id === id) {
      try {
        const detailRes = await getCareSubscriptionDetail(id)
        if (detailRes.code === 200 && detailRes.data) {
          selectedDetail.value = rowToDetail(mapCareSubscriptionDetailToView(detailRes.data))
        }
      } catch (err) {
        console.error('Failed to refresh subscription detail after cancel:', err)
      }
    }
  } catch (err) {
    console.error('Cancel subscription failed:', err)
    showToast('error', 'Network request failed')
  }
}

function onDetailCancel(detail: SubscriptionDetailData): void {
  if (!detail.canCancel) return
  cancelTargetId.value = detail.id
  cancelModalPlan.value = detail.plan
  cancelModalOpen.value = true
}

async function onViewLinkedOrders(detail: SubscriptionDetailData): Promise<void> {
  try {
    const response = await postCareSubscriptionLinkedOrders(detail.id)
    if (response.code !== 200) {
      showToast('error', response.msg || 'Failed to load linked orders')
      return
    }
    writeHmCareOrdersListPrefill({
      searchField: 'subscription_id',
      searchQuery: detail.subscriptionId,
    })
    void router.push(careOrdersListRouteForSub('hm_orders'))
  } catch (err) {
    console.error('Linked orders failed:', err)
    showToast('error', 'Network request failed')
  }
}

function onCancel(row: CareSubscriptionRowView): void {
  openCancelModal(row)
}

function onDocClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (!target.closest('.search-select-wrapper')) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  void beginDataLoad()
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
})
</script>

<style scoped>
.subscriptions-page {
  width: 100%;
  padding: 24px 0 24px 24px;
  box-sizing: border-box;
  height: auto;
  overflow: visible;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0 0 16px 0;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8e8e8;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 24px;
  background: #fff;
  width: 273px;
  height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.search-wrapper:focus-within,
.search-wrapper.is-open {
  border-color: #666666;
}

.search-select-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 12px;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.search-select-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 16px;
  background-color: #e8e8e8;
}

.search-select-label {
  font-size: 14px;
  color: #000000;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}

.dropdown-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.search-select-wrapper.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: -1px;
  width: 273px;
  box-sizing: border-box;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-item {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  font-weight: 600;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  background: transparent;
  color: #000000;
  padding-left: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}

.search-input::placeholder {
  color: #cccccc;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
}

.search-buttons {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.btn {
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  height: 32px;
  width: 80px;
}

.btn-search {
  background: #000;
  color: #fff;
}

.btn-search:hover {
  background: #484848;
}

.btn-clear {
  background: #b0b0b0;
  color: #fff;
}

.btn-clear:hover {
  background: #cccccc;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.list-body {
  position: relative;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

.table-section {
  background: #fff;
  border-radius: 16px;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.table-scroll {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}

.table-scroll::-webkit-scrollbar {
  display: none;
}

.subs-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.col-mobile {
  width: 15.973%;
}

.col-address {
  width: 29.95%;
}

.col-hm-care {
  width: 13.311%;
}

.col-effective {
  width: 15.973%;
}

.col-status {
  width: 12.229%;
}

.col-actions {
  width: 12.563%;
}

.subs-table th {
  box-sizing: border-box;
  height: 56px;
  padding: 20px 16px;
  background: #ffffff;
  box-shadow: inset 0px -1px 0px 0px #e0e0e0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-weight: bold;
  font-size: 14px;
  color: #000000;
  line-height: 16px;
  font-style: normal;
  vertical-align: middle;
  white-space: nowrap;
}

.subs-table th.th-start {
  text-align: left;
  border-radius: 16px 0 0 0;
}

.subs-table th.th-address {
  text-align: left;
}

.subs-table th.th-mid {
  text-align: right;
}

.subs-table th.th-center {
  text-align: center;
}

.subs-table th.th-end {
  text-align: left;
  border-radius: 0 16px 0 0;
}

.subs-table td {
  box-sizing: border-box;
  padding: 20px 16px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 16px;
  font-style: normal;
  vertical-align: middle;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.subs-table tbody tr:last-child td {
  border-bottom: none;
}

.subs-table td.td-left {
  text-align: left;
}

.subs-table td.td-right {
  text-align: right;
}

.subs-table td.td-center {
  text-align: center;
}

.subs-table td.td-actions {
  text-align: left;
}

.subs-table th.th-mobile {
  padding-left: 18px;
  padding-right: 18px;
}

.subs-table td.td-mobile {
  padding-left: 24px;
  padding-right: 24px;
}

.subs-table th.th-address {
  padding-left: 24px;
}

.subs-table td.td-address {
  padding-left: 24px;
  padding-right: 79px;
  white-space: normal;
}

.subs-table th.th-hm-care,
.subs-table td.td-hm-care,
.subs-table th.th-effective,
.subs-table td.td-effective,
.subs-table th.th-status,
.subs-table td.td-status {
  padding-left: 16px;
  padding-right: 16px;
}

.subs-table th.th-actions,
.subs-table td.td-actions {
  padding-left: 49px;
  padding-right: 49px;
}

.status-badge {
  display: block;
  width: 100%;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #00aeef;
  line-height: 16px;
  text-align: center;
  font-style: normal;
}

.status-badge.status-stacked {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: auto;
  height: auto;
}

.status-badge.status-stacked .status-stacked-line {
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  font-style: normal;
  white-space: nowrap;
}

.status-badge.ending-soon .status-stacked-line {
  color: #00aeef;
}

.status-badge.inactive {
  color: #e54848;
}

.status-badge.inactive .status-stacked-line {
  color: #e54848;
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: 16px;
}

.action-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.action-icon-btn.disabled,
.action-icon-btn:disabled {
  cursor: default;
}

.action-icon-btn img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.pagination-wrapper {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}
</style>
